import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

// Extend dayjs with required plugins
dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

export interface WorkDayPayment {
  hoursWorked: number;
  regularHours: number;
  overtimeHours: number;
  saturdayHours: number;
  sundayHours: number;
  holidayHours: number;
  regularPay: number;
  overtimePay: number;
  saturdayPay: number;
  sundayPay: number;
  holidayPay: number;
  totalPay: number;
}

export interface PaymentRates {
  baseRate: number;
  regularHours: number;
  overtimeMultiplier: number;
  saturdayMultiplier: number;
  sundayMultiplier: number;
  holidayMultiplier: number;
  casualLoading: number;
  penaltyRates: {
    weekday: {
      regularHours: {
        threshold: number;
        multiplier: number;
      };
      tier1OT: {
        threshold: number;
        multiplier: number;
      };
      tier2OT: {
        multiplier: number;
      };
    };
    saturday: {
      regularHours: {
        threshold: number;
        multiplier: number;
      };
      tier1OT: {
        threshold: number;
        multiplier: number;
      };
      tier2OT: {
        multiplier: number;
      };
    };
    sunday: {
      regularHours: {
        threshold: number;
        multiplier: number;
      };
      tier1OT: {
        threshold: number;
        multiplier: number;
      };
      tier2OT: {
        multiplier: number;
      };
    };
    publicHoliday: {
      regularHours: {
        threshold: number;
        multiplier: number;
      };
      tier1OT: {
        threshold: number;
        multiplier: number;
      };
      tier2OT: {
        multiplier: number;
      };
    };
  };
}

/**
 * Calculates payment values for hours worked on a given work day
 * @param date - The date of work
 * @param hoursWorked - Total hours worked on the day
 * @param rates - Payment rates configuration
 * @param isHoliday - Whether the day is a holiday
 * @returns WorkDayPayment object with detailed breakdown
 */
export function calculateWorkDayPayment(
  date: Date | dayjs.Dayjs | string,
  hoursWorked: number,
  rates: PaymentRates,
  isHoliday: boolean = false
): WorkDayPayment {
  const workDate = dayjs(date);
  const isSunday = workDate.day() === 0;
  const isSaturday = workDate.day() === 6;

  // Calculate regular hours (first X hours based on configuration)
  const regularHours = Math.min(hoursWorked, rates.regularHours);
  const remainingHours = Math.max(0, hoursWorked - rates.regularHours);

  // Calculate overtime hours
  const overtimeHours = remainingHours;

  // Calculate weekend and holiday hours
  const saturdayHours = isSaturday ? hoursWorked : 0;
  const sundayHours = isSunday ? hoursWorked : 0;
  const holidayHours = isHoliday ? hoursWorked : 0;

  // Calculate payments
  const regularPay = regularHours * rates.baseRate;
  const overtimePay = overtimeHours * rates.baseRate * rates.overtimeMultiplier;
  const saturdayPay = saturdayHours * rates.baseRate * rates.saturdayMultiplier;
  const sundayPay = sundayHours * rates.baseRate * rates.sundayMultiplier;
  const holidayPay = holidayHours * rates.baseRate * rates.holidayMultiplier;

  const totalPay = regularPay + overtimePay + saturdayPay + sundayPay + holidayPay;

  return {
    hoursWorked,
    regularHours,
    overtimeHours,
    saturdayHours,
    sundayHours,
    holidayHours,
    regularPay,
    overtimePay,
    saturdayPay,
    sundayPay,
    holidayPay,
    totalPay
  };
}

/**
 * Calculates payment for a shift that spans multiple days
 * @param startTime - Shift start time
 * @param endTime - Shift end time
 * @param rates - Payment rates configuration
 * @param isHoliday - Optional function to check if a date is a holiday
 * @returns Array of WorkDayPayment objects for each day
 */
export function calculateShiftPayment(
  startTime: Date | dayjs.Dayjs | string,
  endTime: Date | dayjs.Dayjs | string,
  rates: PaymentRates,
  isHoliday?: (date: Date | dayjs.Dayjs) => boolean
): WorkDayPayment[] {
  const start = dayjs(startTime);
  const end = dayjs(endTime);

  const payments: WorkDayPayment[] = [];
  let currentDate = start.startOf('day');

  while (currentDate.isSameOrBefore(end, 'day')) {
    const dayStart = currentDate.startOf('day');
    const dayEnd = currentDate.endOf('day');

    const shiftStart = currentDate.isSame(start, 'day') ? start : dayStart;
    const shiftEnd = currentDate.isSame(end, 'day') ? end : dayEnd;

    // Calculate hours worked and round up to the nearest hour
    const hoursWorked = Math.ceil(shiftEnd.diff(shiftStart, 'hour', true));

    if (hoursWorked > 0) {
      const holidayCheck = isHoliday ? isHoliday(currentDate) : false;
      payments.push(calculateWorkDayPayment(currentDate, hoursWorked, rates, holidayCheck));
    }

    currentDate = currentDate.add(1, 'day');
  }

  return payments;
}

// Set default values for rates if not provided
export const defaultRates = {
  baseRate: 35,
  casualLoading: 0.25, // 25% casual loading
  penaltyRates: {
    'Weekday': {
      regularHours: 1.0,     // First 10 hours
      tier1OT: 1.5,          // 10-12 hours
      tier2OT: 2.0           // >12 hours
    },
    'Saturday': {
      regularHours: 1.5,
      tier1OT: 1.5,
      tier2OT: 2.0
    },
    'Sunday': {
      regularHours: 2.0,
      tier1OT: 2.0,
      tier2OT: 2.0
    },
    'Public Holiday': {
      regularHours: 2.5,
      tier1OT: 2.5,
      tier2OT: 2.5
    }
  }
}

/**
 * Calculates total payment for a shift spanning multiple days
 * @param startTime - Shift start time
 * @param endTime - Shift end time
 * @param rates - Payment rates configuration
 * @param isHoliday - Optional function to check if a date is a holiday
 * @returns Total payment amount
 */
export function calculateTotalShiftPayment(
  startTime: Date | dayjs.Dayjs | string,
  endTime: Date | dayjs.Dayjs | string,
  rates: PaymentRates,
  isHoliday?: (date: Date | dayjs.Dayjs) => boolean
): number {
  rates = {
    ...defaultRates,
    ...rates
  };
  const payments = calculateShiftPayment(startTime, endTime, rates, isHoliday);
  return payments.reduce((total, payment) => total + payment.totalPay, 0);
}
